import { Component, OnInit } from '@angular/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { RankingsService } from '../../services/rankings.service';
import { LimeGearCharts } from 'app/modules/shared/classes/chart-data';
import * as Highcharts from 'highcharts';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { forkJoin } from 'rxjs';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import { LORankingsData } from '../../interfaces/performance-rankings';

@Component({
    selector: 'app-performance-insights-monthly-lo-rankings',
    templateUrl: './performance-insights-monthly-lo-rankings.component.html',
    styleUrls: ['./performance-insights-monthly-lo-rankings.component.scss'],
})
export class PerformanceInsightsMonthlyLoRankingsComponent implements OnInit {
    overallVolData: LORankingsData[];
    overallUnitsData: LORankingsData[];
    filteredVolData: LORankingsData[];
    filteredUnitsData: LORankingsData[];
    unitsGridData: GridDataResult;
    volGridData: GridDataResult;
    pageHeadline: IPageHeadline = {
        module: 'performance',
        moduleIcon: 'fa-heartbeat',
        pageTitle: 'Monthly LO Rankings',
        linkArray: [
            {
                moduleLink: 'performance',
                linkTitle: 'Performance Home',
                showLink: true,
                isHome: true,
            },
            {
                moduleLink: 'performance/insights',
                linkTitle: 'Performance Insights',
                showLink: true,
            },
        ],
    };
    type: 'numeric' | 'input' = 'input';
    pageSize: number = 10;
    skip = { volSkip: 0, unitSkip: 0 };
    loading: boolean = true;
    unitSort: SortDescriptor[] = [{ field: 'rank', dir: 'desc' }];
    volSort: SortDescriptor[] = [{ field: 'rank', dir: 'desc' }];
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };
    pageable = {
        refresh: true,
        info: true,
        type: this.type,
        previousNext: true,
        pageSizes: [5, 10, 20],
    };
    limeGearCharts: LimeGearCharts.LimeGearChart;
    periodData: string[];
    hFilterJSON: string;
    displayOptions: DisplayOptions[];
    asOfDate: string;
    hiddenFilterItems: string[] = ['dateRangeRolling'];
    action: NoDataActionType;
    noDataType: NoDataTypes;
    noData: boolean;
    asOfDateId: string = 'rankingsasof';

    constructor(
        private rankingsService: RankingsService,
        private reusableFunctions: ReusableFunctions,
        private router: Router,
        private decimalPipe: DecimalPipe
    ) {}

    ngOnInit(): void {
        this.action = NoDataActionType.pageStart;
        this.noDataType = {
            action: this.action,
            searchTerm: '',
            componentTitle: this.pageHeadline.pageTitle,
            inputSource: NoDataSourceType.largeDataCard,
        };
        this.hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.limeGearCharts = new LimeGearCharts.LimeGearChart(
            this.router,
            this.reusableFunctions,
            this.decimalPipe
        );
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this.router.url);
        this.setDisplayOptionsSelected();
        this.loadData();
    }

    loadData() {
        const volParams = {
            measure: 'Vol',
            ymc: this.asOfDate,
            hFilterJSON: this.hFilterJSON,
        };
        const unitParams = {
            measure: 'Units',
            ymc: this.asOfDate,
            hFilterJSON: this.hFilterJSON,
        };

        forkJoin([
            this.rankingsService.displayLORankings(volParams),
            this.rankingsService.displayLORankings(unitParams),
        ]).subscribe(
            ([volData, unitsData]) => {
                if (!volData || !unitsData || volData.length < 1 || unitsData.length < 1) {
                    this.setNoData();
                }
                this.overallVolData = volData;
                this.overallUnitsData = unitsData;
                this.getPeriodData(JSON.parse(JSON.stringify(volData)));
                this.prepareGridData(this.overallVolData, this.overallUnitsData);
            },
            (err) => {
                this.setNoData();
                this.loading = false;
                console.log('error: ', err);
            }
        );
    }

    getPeriodData(data: LORankingsData[]) {
        const periodData = new Set<string>();
        data.forEach((emp) => periodData.add(emp.yearMonthCalendar));
        this.periodData = Array.from(periodData).reverse();
    }

    prepareGridData(overallVolData: LORankingsData[], overallUnitsData: LORankingsData[]) {
        const overallDataArr = [overallVolData, overallUnitsData];
        for (let i = 0; i < overallDataArr.length; i++) {
            const filteredData = overallDataArr[i].filter(
                (item) => item.yearMonthCalendar === this.asOfDate
            );

            filteredData.forEach((emp) => {
                const measureValues = overallDataArr[i]
                    .filter(
                        (item) =>
                            item.employee === emp.employee &&
                            item.employeeIdentifier === emp.employeeIdentifier
                    )
                    .map((i) => i.mtdMeasure);
                emp.measureValues = measureValues.reverse();
                emp.prevMTDComp = this.reusableFunctions.getCaret(emp.prevMTDChg, true, null, true);
            });

            if (i === 0) {
                this.filteredVolData = this.reusableFunctions.getFriendlyTenure(filteredData);
            } else {
                this.filteredUnitsData = this.reusableFunctions.getFriendlyTenure(filteredData);
            }
        }

        this.updateVolumeGrid();
        this.updateUnitGrid();
        this.loading = false;
    }

    updateVolumeGrid() {
        const orderedData = orderBy(this.filteredVolData, this.volSort);
        this.volGridData = {
            data: orderedData.slice(this.skip.volSkip, this.skip.volSkip + this.pageSize),
            total: orderedData.length,
        };

        setTimeout(() => {
            this.drawCharts(this.volGridData.data, 'vol', 'currency');
        }, 0);
    }

    updateUnitGrid() {
        const orderedData = orderBy(this.filteredUnitsData, this.unitSort);

        this.unitsGridData = {
            data: orderedData.slice(this.skip.unitSkip, this.skip.unitSkip + this.pageSize),
            total: orderedData.length,
        };

        setTimeout(() => {
            this.drawCharts(this.unitsGridData.data, 'unit', 'number');
        }, 0);
    }

    drawCharts(data: LORankingsData[], type: string, dataType: string): void {
        for (let i = 0; i < data.length; i++) {
            new Highcharts.Chart(
                `${type}mtdMeasure${data[i].employee}`,
                this.limeGearCharts.createRevenueSparkline(
                    data[i].measureValues,
                    this.periodData,
                    'area',
                    dataType
                )
            );
        }
    }

    sortChange(sort: SortDescriptor[], gridType: string): void {
        if (gridType === 'vol') {
            this.volSort = sort;
            this.updateVolumeGrid();
        } else {
            this.unitSort = sort;
            this.updateUnitGrid();
        }
    }

    pageChange(event: PageChangeEvent, gridType: string): void {
        this.pageSize = event.take;
        if (gridType === 'vol') {
            this.skip.volSkip = event.skip;
        } else {
            this.skip.unitSkip = event.skip;
        }
        this.updateVolumeGrid();
        this.updateUnitGrid();
    }

    applyNewFilters(event: string) {
        this.action = NoDataActionType.filter;
        this.hFilterJSON = event;
        this.loadData();
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.loading = true;
        this.skip.volSkip = 0;
        this.skip.unitSkip = 0;
        this.displayOptions = event;
        this.setDisplayOptionsSelected();
        this.loadData();
    }

    setDisplayOptionsSelected() {
        const asOfDate = this.displayOptions.find((item) => item.optionKey === 'rankingsAsOf');
        this.asOfDate = asOfDate?.selectedOptions ?? '';
    }

    setNoData() {
        this.noData = true;
        this.loading = false;
        this.noDataType.action = this.action;
    }
}
